import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, ChevronDown } from 'lucide-react'
import { Input, inputVariants } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { Text } from '@/components/ui/Text'
import type { SocialTrackerLanguageFieldProps } from '@/types/components/social-tracker-dialog'
import catalog from './socialLanguageCatalog.json'

export function SocialTrackerLanguageField({
  value,
  onChange,
  invalid,
}: SocialTrackerLanguageFieldProps) {
  const { t, i18n } = useTranslation('app')
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const optionsRef = useRef<HTMLDivElement>(null)
  const options = useMemo(() => {
    const names = new Intl.DisplayNames([i18n.language], {
      type: 'language',
      fallback: 'none',
    })
    const entries = catalog.languages.map((entry) => {
      const localized = names.of(entry.code)
      return {
        ...entry,
        label: localized && localized !== entry.code ? localized : entry.name,
      }
    })
    // Keep any saved locale outside the catalog selectable; never rewrite its code.
    if (value && !entries.some((entry) => entry.code === value)) {
      let label = value
      try {
        label = names.of(value) ?? value
      } catch {
        /* Preserve legacy literal values. */
      }
      entries.push({ code: value, name: label, label })
    }
    return entries.sort((a, b) => a.label.localeCompare(b.label, i18n.language))
  }, [i18n.language, value])
  const query = search.trim().toLocaleLowerCase(i18n.language)
  const filtered = options.filter(
    (entry) =>
      entry.label.toLocaleLowerCase(i18n.language).includes(query) ||
      entry.name.toLowerCase().includes(query),
  )
  const selected = options.find((entry) => entry.code === value)
  function choose(code: string) {
    onChange(code)
    setOpen(false)
    setSearch('')
  }
  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) setSearch('')
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          id="social-tracker-language"
          role="combobox"
          aria-expanded={open}
          aria-controls="social-tracker-languages"
          aria-invalid={invalid || undefined}
          aria-describedby={
            invalid ? 'social-tracker-language-error' : undefined
          }
          data-testid="social-tracker-language-input"
          className={cn(
            inputVariants(),
            'items-center justify-between gap-3 text-left',
          )}
        >
          <span className="truncate">
            {selected?.label ?? t('socialListening.form.selectLanguage')}
          </span>
          <ChevronDown
            className="h-4 w-4 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] p-2"
      >
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          aria-label={t('socialListening.form.searchLanguages')}
          placeholder={t('socialListening.form.searchLanguages')}
          aria-controls="social-tracker-languages"
          className="h-9"
          onKeyDown={(event) => {
            if (event.nativeEvent.isComposing) return
            if (event.key === 'ArrowDown') {
              event.preventDefault()
              optionsRef.current
                ?.querySelector<HTMLButtonElement>('button')
                ?.focus()
            }
            if (event.key === 'Enter' && filtered.length) {
              event.preventDefault()
              choose(filtered[0].code)
            }
          }}
        />
        <div
          ref={optionsRef}
          id="social-tracker-languages"
          role="listbox"
          aria-label={t('socialListening.form.language')}
          className="max-h-60 overflow-y-auto pt-1"
        >
          {filtered.map((entry) => (
            <button
              key={entry.code}
              type="button"
              role="option"
              aria-selected={value === entry.code}
              className="flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-sm text-foreground hover:bg-accent focus:bg-accent focus:outline-none"
              onClick={() => choose(entry.code)}
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                  event.preventDefault()
                  const adjacent =
                    event.key === 'ArrowDown'
                      ? event.currentTarget.nextElementSibling
                      : event.currentTarget.previousElementSibling
                  if (adjacent instanceof HTMLButtonElement) adjacent.focus()
                }
              }}
            >
              <span className="truncate">{entry.label}</span>
              {value === entry.code ? (
                <Check className="h-4 w-4 shrink-0" aria-hidden="true" />
              ) : null}
            </button>
          ))}
          {!filtered.length ? (
            <Text size="sm" color="muted" className="p-3">
              {t('socialListening.form.noLanguages')}
            </Text>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  )
}
